import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, CircleCheck, Download, History, Sparkles, Wand2 } from "lucide-react";
import { DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { isWindows, rememberHubReady } from "@/lib/hubReady";
import { DRIVER_STEPS, FIRMWARE_STEPS, NO_DRIVER_STEP } from "./readySteps";

const QUESTIONS = {
  first: {
    title: "Is this your first time using Pybricks or HubPilot?",
    hint: "Your hub needs the free Pybricks firmware before it can pair with an Xbox controller. Let's check you're ready.",
    answers: [
      { id: "yes", icon: Sparkles, t: "Yes, it's my first time", d: "We'll set up the USB driver and install Pybricks firmware together, step by step. About 10 minutes." },
      { id: "no", icon: History, t: "No, I've used it before", d: "We'll just check your hub has Pybricks firmware." },
    ],
  },
  installed: {
    title: "Is the official Pybricks firmware installed on your hub?",
    hint: "Hubs straight from the box run LEGO's firmware, which can't pair with an Xbox controller.",
    answers: [
      { id: "yes", icon: CircleCheck, t: "Yes, it's installed", d: "Pybricks v3.3 or newer from code.pybricks.com. Go straight to setting up your robot." },
      { id: "no", icon: Download, t: "No, or I'm not sure", d: "We'll show you how to install it, step by step. Choose this if your hub has HubPilot firmware too. About 5 minutes." },
    ],
  },
};
const SECTIONS = { driver: "USB driver", firmware: "Pybricks firmware" };

const Answer = ({ a, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`ready-answer-${a.id}`}
    className="rc-card group flex flex-col rounded-2xl border bg-card p-6 text-left transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-[0_18px_40px_-24px_hsl(var(--primary)/0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-secondary-foreground transition-colors group-hover:bg-primary group-hover:text-primary-foreground"><a.icon className="h-5 w-5" /></span>
    <p className="mt-4 font-display text-lg font-bold">{a.t}</p>
    <p className="mt-1 text-sm text-muted-foreground">{a.d}</p>
  </button>
);

const SectionChips = ({ steps, i }) => {
  const sections = [...new Set(steps.map((s) => s.section))];
  const current = steps[i].section;
  return (
    <ol className="mt-4 flex flex-wrap items-center gap-2" aria-label="Guide sections">
      {sections.map((s, n) => (
        <li key={s} data-testid={`ready-section-${s}`} className={cn("rounded-full border px-3 py-1.5 text-xs font-semibold", s === current ? "border-primary bg-primary text-primary-foreground" : sections.indexOf(current) > n ? "border-primary/40 bg-secondary text-secondary-foreground" : "text-muted-foreground")}>
          {n + 1}. {SECTIONS[s]}
        </li>
      ))}
      <li className="ml-1 flex gap-1" aria-hidden="true">
        {steps.map((s, n) => <span key={s.id} className={cn("h-1.5 w-4 rounded-full", n <= i ? "bg-primary" : "bg-muted")} />)}
      </li>
    </ol>
  );
};

export const HubReadyFlow = ({ onReady, onSkip, onCancel, cancelLabel = "Cancel" }) => {
  const [screen, setScreen] = useState("first");
  const [firstTime, setFirstTime] = useState(false);
  const [win, setWin] = useState(isWindows);
  const [i, setI] = useState(0);
  const steps = useMemo(() => [...(firstTime ? (win ? DRIVER_STEPS : [NO_DRIVER_STEP]) : []), ...FIRMWARE_STEPS], [firstTime, win]);
  const bodyRef = useRef(null);
  useEffect(() => { bodyRef.current?.scrollTo(0, 0); }, [screen, i, win]);

  const ready = () => { rememberHubReady(); onReady(); };
  const startGuide = (first) => { setFirstTime(first); setI(0); setScreen("guide"); };
  const answer = (id) => {
    if (screen === "first") return id === "yes" ? startGuide(true) : setScreen("installed");
    return id === "yes" ? ready() : startGuide(false);
  };

  const q = QUESTIONS[screen];
  const step = steps[i];
  const sectionSteps = step ? steps.filter((s) => s.section === step.section) : [];
  const last = i === steps.length - 1;
  const back = () => (screen === "installed" ? setScreen("first") : i > 0 ? setI(i - 1) : setScreen(firstTime ? "first" : "installed"));

  return (
    <>
      <div className="border-b px-5 pb-5 pt-6 sm:px-7">
        <p className="eyebrow pr-8 text-accent dark:text-primary" data-testid="ready-eyebrow">
          {q ? "Before you start" : `${SECTIONS[step.section]} · Step ${sectionSteps.indexOf(step) + 1} of ${sectionSteps.length}`}
        </p>
        <DialogTitle className="mt-2 font-display text-lg font-extrabold tracking-tight" data-testid="ready-title">{q ? q.title : step.title}</DialogTitle>
        <DialogDescription className="mt-1">{q ? q.hint : "Follow each step, then press Next."}</DialogDescription>
        {!q && <SectionChips steps={steps} i={i} />}
      </div>
      <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-7 md:min-h-[360px]" data-testid="ready-body">
        {q ? (
          <div className="grid gap-4 sm:grid-cols-2" data-testid={`ready-question-${screen}`}>
            {q.answers.map((a) => <Answer key={a.id} a={a} onClick={() => answer(a.id)} />)}
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-[1fr_300px]" data-testid={`ready-step-${step.id}`}>
            <div className="space-y-3 text-sm leading-relaxed text-muted-foreground sm:text-base"><step.Body afterDriver={firstTime} showWindows={() => setWin(true)} /></div>
            <div className="order-first md:order-none"><step.Visual /></div>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 border-t bg-muted/30 px-5 py-4 sm:px-7">
        <Button variant="ghost" className="rounded-full" onClick={screen === "first" ? onCancel : back} data-testid="ready-back">
          <ArrowLeft className="mr-1.5 h-4 w-4" />{screen === "first" ? cancelLabel : "Back"}
        </Button>
        {q ? (
          <Button variant="ghost" className="rounded-full text-muted-foreground" onClick={onSkip} data-testid="ready-skip">Skip for now<ArrowRight className="ml-1.5 h-4 w-4" /></Button>
        ) : last ? (
          <Button onClick={ready} className="rounded-full bg-accent px-6 text-accent-foreground hover:bg-accent/90" data-testid="ready-done">
            <Wand2 className="mr-1.5 h-4 w-4" /><span className="sm:hidden">Set up my robot</span><span className="hidden sm:inline">My hub is ready: set up my robot</span>
          </Button>
        ) : (
          <Button onClick={() => setI(i + 1)} className="rounded-full px-6" data-testid="ready-next">Next<ArrowRight className="ml-1.5 h-4 w-4" /></Button>
        )}
      </div>
    </>
  );
};
